import { formatDeliveryStatus } from '../constants/delivery-status.js';
/** Les champs qui ont RÉELLEMENT bougé entre les deux images. L'union des clés, pas seulement celles de
 *  `before` : une colonne apparue dans `after` est un changement, et la manquer serait un mensonge. */
export function diffMutation(before, after) {
    const b = (before ?? {});
    const a = (after ?? {});
    if (typeof b !== 'object' || typeof a !== 'object')
        return [];
    const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].sort();
    const out = [];
    for (const k of keys) {
        // Comparaison sur la forme JSON : les valeurs viennent d'une colonne jsonb, donc les dates y sont
        // déjà des chaînes et les nombres des nombres. `===` suffirait pour les scalaires mais raterait
        // les objets imbriqués (rares mais réels : `details`, `cells`).
        if (JSON.stringify(b[k]) !== JSON.stringify(a[k]))
            out.push({ column: k, before: b[k], after: a[k] });
    }
    return out;
}
/** La comptabilité interne : ces colonnes bougent à CHAQUE écriture et n'apprennent rien sur ce qui
 *  vient d'être décidé. Elles restent affichées dans le tableau de diff — elles ont leur utilité quand
 *  on enquête — mais ne peuvent pas servir de résumé. */
const LOW_SIGNAL_COLUMNS = new Set([
    'updated_at', 'updatedAt', 'created_at', 'createdAt',
    'mappingUpdatedAt', 'mapping_updated_at',
    'validatedAt', 'validated_at', 'validatedById', 'validated_by_id',
    'ownerAt', 'owner_at', 'ownerId', 'owner_id',
    'lastTicketId', 'last_ticket_id',
]);
/** Le résumé d'une action, pour la ligne fermée : « 3 champs · public.address · delivery_status 7013 → 7015 ».
 *  Ce que `summary` aurait dû contenir — la colonne existe en base mais elle est VIDE sur les 1 563
 *  lignes (mesuré le 31-08), donc l'afficher ne donnait rien. On le calcule au lieu de l'attendre. */
export function summariseMutations(mutations) {
    const list = Array.isArray(mutations) ? mutations : [];
    let changedFields = 0;
    let headline = null;
    const tables = [];
    for (const m of list) {
        if (m.table && !tables.includes(m.table))
            tables.push(m.table);
        if (m.op === 'insert') {
            if (headline == null)
                headline = `création dans ${m.table ?? '?'}`;
            continue;
        }
        if (m.op === 'delete') {
            if (headline == null)
                headline = `suppression dans ${m.table ?? '?'}`;
            continue;
        }
        const changes = diffMutation(m.before, m.after);
        changedFields += changes.length;
        if (headline == null && changes.length > 0) {
            // Le PREMIER changement par ordre alphabétique n'est presque jamais le plus parlant : sur une
            // validation réelle il donnait « mappingUpdatedAt ∅ → 2026-08-27 » là où l'information est
            // « Statut ESCALATED → VALIDATED ». On écarte donc d'abord la comptabilité interne.
            const c = changes.find((x) => !LOW_SIGNAL_COLUMNS.has(x.column)) ?? changes[0];
            const label = COLUMN_LABELS[c.column] ?? c.column;
            headline = `${label} ${formatLogValue(c.column, c.before)} → ${formatLogValue(c.column, c.after)}`;
        }
    }
    return { changedFields, headline, tables };
}
// ── Étiqueter une action ──────────────────────────────────────────────────────────────────────────
//
// 653 lignes portent `action = 'unknown'`. Le comptage brut laissait croire à 653 écritures mal
// nommées ; la réalité mesurée le 31-08 est tout autre : **641 sont des prises de verrou de la fenêtre
// de validation** (`POST /tickets/:id/lock`), un bruit déjà coupé à la source depuis le 2026-08-06 —
// ce sont des lignes d'historique. Les 12 restantes sont six routes légitimes auxquelles il manque
// simplement un appel à recordAudit. D'où ce repli par CHEMIN plutôt qu'un chantier de renommage.
const PATH_LABELS = [
    [/\/tickets\/[^/]+\/lock$/, 'Verrou de la fenêtre'],
    [/\/admin\/users$/, 'Création d’utilisateur'],
    [/\/admin\/settings\/escalation-target$/, 'Réglage — cible d’escalade'],
    [/\/admin\/settings\/build-target$/, 'Réglage — cible Build'],
    [/\/tickets\/[^/]+\/ask-operator$/, 'Question à l’opérateur'],
    [/\/tickets\/[^/]+\/attachments$/, 'Pièce jointe'],
];
/** Un libellé lisible : l'action quand elle est nommée, sinon le chemin reconnu, sinon le chemin brut.
 *  Ne renvoie JAMAIS « unknown » — c'est précisément ce mot qui rendait le journal illisible. */
export function actionLabelFor(action, method, path) {
    if (action && action !== 'unknown')
        return action;
    for (const [re, label] of PATH_LABELS)
        if (re.test(path))
            return label;
    return `${method} ${path.replace(/^\/internal\/v1/, '')}`;
}
/** Vrai pour les lignes qui ne sont QUE du bruit d'historique : la prise de verrou, coupée à la source
 *  le 2026-08-06 mais encore présente 641 fois dans le passé. Sert au filtre « masquer le bruit ». */
export function isNoiseAction(action, path) {
    return (action === 'unknown' || !action) && /\/tickets\/[^/]+\/lock$/.test(path);
}
// ── Rendre une valeur lisible ─────────────────────────────────────────────────────────────────────
/** Libellés métier des colonnes les plus lues. Le nom BRUT de la colonne reste affiché à côté : c'est
 *  lui qu'on cherche dans le SQL et dans le bloc des 105 colonnes, le supprimer coûterait plus qu'il
 *  ne rapporte. Une colonne absente de la table garde simplement son nom. */
export const COLUMN_LABELS = {
    approved_for_release: 'Approuvée pour publication',
    home_released: 'Home released',
    home_released_date: 'Date de publication',
    out_of_scope_at: 'Retirée du scope le',
    scopelist_v15: 'Deployment list',
    scopelist_v2: 'Build list',
    belmap_version: 'Belmap version (1 = PST bis)',
    delivery_status: 'Statut de livraison',
    status: 'Statut',
    dwelling_unit_type: 'Type de logement',
    area_id: 'Area ID',
    area_pop_id: 'Area POP ID',
    central_pop_id: 'Central POP ID',
    length_estimate_from_street: 'Distance depuis la rue',
    distance_source: 'Source de la distance',
    quote_required: 'Devis requis',
    bus_number: 'Numéro de boîte',
    house_number: 'Numéro',
    house_number_extension: 'Extension',
    street_name: 'Rue',
    postal_code: 'Code postal',
    city: 'Ville',
    municipality: 'Commune',
    twin_jvid: 'Twin JVID',
    remark: 'Message',
    updated_at: 'Modifié le',
};
/** Une valeur prête à lire. `null` devient « ∅ » et JAMAIS la chaîne « null », qu'on confond avec une
 *  vraie valeur ; les codes de livraison sont décodés avec le catalogue déjà écrit pour le sélecteur ;
 *  les dates perdent leur bruit machine. */
export function formatLogValue(column, v) {
    if (v === null || v === undefined || v === '')
        return '∅';
    if (typeof v === 'boolean')
        return v ? '✓ oui' : '✗ non';
    if (column === 'delivery_status' && typeof v === 'number')
        return formatDeliveryStatus(v);
    if (typeof v === 'string') {
        // Horodatage ISO → « 2026-08-31 14:11 ». On garde la date seule quand il n'y a pas d'heure utile.
        const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(v);
        if (m)
            return m[2] === '00:00' ? m[1] : `${m[1]} ${m[2]}`;
        return v.length > 120 ? `${v.slice(0, 117)}…` : v;
    }
    if (typeof v === 'object')
        return JSON.stringify(v);
    return String(v);
}
//# sourceMappingURL=action-diff.js.map